import { useEffect, useId, useMemo, useState } from "react";
import { setSecurityQuestion } from "../../../services/authApi";
import { Input, Modal } from "@iq/models";
import "./Modal.css";
import { MdSecurity } from "react-icons/md";

const PRESET_QUESTIONS = [
  "What was the name of your first pet?",
  "What was the name of the street you grew up on?",
  "What was your mother's maiden name?",
  "What was the name of your elementary school?",
  "What city were you born in?",
  "What is your oldest sibling's middle name?",
  "What was the make and model of your first car?",
  "What was your childhood nickname?",
  "Other — type your own question",
];

function SecurityQuestionSetup({
  profile,
  onComplete,
  onBack,
  initialQuestion = "",
  submitLabel = "Set Security Question",
  subtitle = "Set up a security question to verify your identity when you log in.",
}) {
  const formId = useId();
  const [selectedQuestion, setSelectedQuestion] = useState("");
  const [customQuestion, setCustomQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [confirmAnswer, setConfirmAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!initialQuestion) {
      setSelectedQuestion("");
      setCustomQuestion("");
      setAnswer("");
      setConfirmAnswer("");
      return;
    }

    const trimmedQuestion = initialQuestion.trim();
    const presetMatch = PRESET_QUESTIONS.find((question) => question === trimmedQuestion);

    if (presetMatch) {
      setSelectedQuestion(trimmedQuestion);
      setCustomQuestion("");
    } else {
      setSelectedQuestion("Other — type your own question");
      setCustomQuestion(trimmedQuestion);
    }

    setAnswer("");
    setConfirmAnswer("");
  }, [initialQuestion]);

  const isOther = selectedQuestion === "Other — type your own question";
  const finalQuestion = isOther ? customQuestion.trim() : selectedQuestion;
  const canSubmit = !loading && Boolean(finalQuestion) && Boolean(answer.trim()) && Boolean(confirmAnswer.trim());

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (!finalQuestion) {
      setError("Please select or enter a security question.");
      return;
    }
    if (!answer.trim()) {
      setError("Please enter an answer.");
      return;
    }
    if (answer.trim().toLowerCase() !== confirmAnswer.trim().toLowerCase()) {
      setError("Answers do not match. Please try again.");
      return;
    }

    setLoading(true);
    try {
      await setSecurityQuestion(profile.id, finalQuestion, answer.trim());
      onComplete();
    } catch (err) {
      setError(err.message || "Could not save security question.");
    } finally {
      setLoading(false);
    }
  }

  const actionButtons = useMemo(() => ([
      {
          key: "save",
          text: loading ? "Saving..." : submitLabel,
          icon: <MdSecurity />,
          type: "submit",
          form: formId,
              disabled: !canSubmit,
          color: "accent",
      },
            ]), [canSubmit, formId, loading, submitLabel]);

  return (
    <Modal 
        isOpen={true} 
        onClose={onBack} 
        title="Security Question"
        icon={<MdSecurity />}
      actionButtons={actionButtons}
      errorMessage={error}
      >   
      <div className="sq-card">
        <div className="sq-header">
          <h2 className="sq-name">{profile.name}</h2>
        </div>

        <p className="sq-subtitle">
          {subtitle}
        </p>

        <form id={formId} onSubmit={handleSubmit} className="sq-form">
          <div className="sq-field">
            <label className="sq-label">Security Question</label>
            <Input
              type="dropdown"
              value={selectedQuestion}
              placeholder="Choose a question"
              allowCustom={false}
              colorScheme="light"
              ariaLabel="Security question options"
              options={PRESET_QUESTIONS.map((question) => ({ value: question, label: question }))}
              onOptionSelect={(option) => {
                setSelectedQuestion(option.value);
                setError("");
              }}
            />
          </div>

          {isOther && (
            <div className="sq-field">
              <label className="sq-label">Your Question</label>
              <Input
                fieldClassName="sq-input"
                type="text"
                placeholder="Type your own security question"
                value={customQuestion}
                onChange={(e) => setCustomQuestion(e.target.value)}
                autoComplete="off"
                colorScheme="light"
              />
            </div>
          )}

          <div className="sq-field">
            <label className="sq-label">Answer</label>
            <Input
              fieldClassName="sq-input"
              type="password"
              placeholder="Your answer"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              autoComplete="off"
              colorScheme="light"
            />
          </div>

          <div className="sq-field">
            <label className="sq-label">Confirm Answer</label>
            <Input
              fieldClassName="sq-input"
              type="password"
              placeholder="Re-enter your answer"
              value={confirmAnswer}
              onChange={(e) => setConfirmAnswer(e.target.value)}
              autoComplete="off"
              colorScheme="light"
            />
          </div>
        </form>
      </div>
        
    </Modal>
  );
}

export default SecurityQuestionSetup;